import { Link } from "react-router-dom";
import { ORG } from "../lib/constants";

export const PRIVACY_EFFECTIVE = "June 1, 2026";

// Every statement below describes how this website actually works today.
const SECTIONS = [
  ["Who we are", `${ORG.name} is a Christ-centered nonprofit at ${ORG.address}, providing supportive and transitional living for women. This notice explains how we handle information you share through this website.`],
  ["Information we collect", [
    "Contact form: your name, email, phone number (optional), subject, and message.",
    "Housing application: contact details, housing history, local connections, references, employment, finances, children, emergency contact, and your typed signature. You may also choose to share sensitive details — Social Security, CDIB and DOC numbers, date of birth, medications, and legal history — and upload documents such as an ID or release paperwork. Only fields marked * are required.",
    "Your privacy acknowledgement: the date and time you acknowledged this notice and which version you saw.",
    "Chat assistant: the questions you type.",
    "Basic website usage data collected by our hosting platform's analytics. Screen recording is switched off on the application and staff pages.",
  ]],
  ["How we use it", "Only to review your application, contact you and the people you list (such as references, your emergency contact, and a supervising officer where relevant), prepare program paperwork, provide program services, and answer your messages. We do not sell or rent your information, and we do not use it for advertising."],
  ["How we protect it", [
    "Sensitive application details and uploaded documents are encrypted when stored. Each document has its own encryption key.",
    "Email notifications to our staff never include application details — only that a new application arrived.",
    "Only authorized ministry staff can view applications. Each person has their own login with two-step verification, sessions expire automatically, and every view of protected information is recorded.",
    "Information travels over encrypted (HTTPS) connections.",
  ]],
  ["Who receives information", [
    "Our website hosting, database, and file-storage provider (Emergent), which stores the site and your encrypted information on our behalf.",
    "Our email service (Resend), which delivers contact messages and new-application alerts to our staff inbox. Application details are never included.",
    "Our chat assistant provider (OpenAI), which receives only what you type into the chat. Please don't share personal details there.",
    "Google Fonts, which supplies the lettering on this site and may receive your device's IP address.",
    "We may disclose information if the law requires it.",
  ]],
  ["Other websites", "Donation, event RSVP, and social media links take you to other websites (such as Donorbox, Eventbrite, and Facebook), which have their own privacy policies."],
  ["How long we keep it", "We keep applications only as long as our staff need them; they are then permanently deleted by an authorized staff member, and their uploaded documents are permanently erased with them. Contact messages are kept with our ministry correspondence."],
  ["Your choices", `Every application question without a * is optional, and you can share details by phone instead. You may ask to see, correct, or delete your information by calling ${ORG.phone} or emailing ${ORG.email}.`],
  ["Children", "This website is intended for adults. Information about children is collected only when an applicant chooses to tell us about her own children."],
  ["Changes to this notice", "If we change how we handle information, we will update this page and its effective date."],
  ["Contact us", `${ORG.name} · ${ORG.address} · ${ORG.phone} · ${ORG.email}`],
];

function Body({ body }) {
  if (Array.isArray(body)) {
    return (
      <ul className="mt-2 space-y-2 list-disc pl-5 text-brand-charcoal/90 leading-relaxed">
        {body.map((b) => <li key={b}>{b}</li>)}
      </ul>
    );
  }
  return <p className="mt-2 text-brand-charcoal/90 leading-relaxed">{body}</p>;
}

export default function PrivacyPage() {
  return (
    <div className="container-editorial pt-20 pb-24 md:pt-24" data-testid="privacy-page">
      <div className="max-w-3xl">
        <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-semibold">Privacy</div>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-brand-charcoal tracking-tight">Privacy Notice</h1>
        <p className="mt-4 text-sm text-brand-muted" data-testid="privacy-effective-date">Effective {PRIVACY_EFFECTIVE}</p>
        <div className="mt-10 space-y-8">
          {SECTIONS.map(([title, body], i) => (
            <section key={title} aria-labelledby={`privacy-s${i}`}>
              <h2 id={`privacy-s${i}`} className="font-serif text-2xl text-brand-charcoal">{title}</h2>
              <Body body={body} />
            </section>
          ))}
        </div>
        <p className="mt-12 text-sm text-brand-muted">
          Ready to apply? <Link to="/apply" className="text-brand-teal underline underline-offset-4">Start your application</Link>.
        </p>
      </div>
    </div>
  );
}
